import { useEffect, useMemo, useRef } from 'react'

import { useFetchWorkOrderFacetsQuery } from 'api/work-order/fetchWorkOrderFacets'
import { UNASSIGNED_ROUTE } from 'containers/new-route-manager/settings'
import { useDebounced } from 'hooks/useDebounced'
import { formatDateToBEFormatDateFns } from 'utils/date'

import { WORK_ORDER_STATUS_FILTER_MENU_OPTIONS, WORK_ORDER_STATUS_IDS } from './filters/work-order-status-filter/settings'
import { isEverythingSelected } from './utils'

import type { WorkOrderFacetsApi, WorkOrderFacetsRequestApi } from 'types/api/work-order/workOrderFacets'

const FILTER_DEBOUNCE_MS = 300

interface FilterOption {
  id: string
  value?: string
}

interface Params {
  accountId?: string
  locationId?: string
  configuredServiceId?: string
  selectedServices?: FilterOption[]
  selectedRoutes?: FilterOption[]
  selectedWorkOrderStatuses?: FilterOption[]
  selectedDateRange?: [Date | null, Date | null]
}

const EMPTY_FACETS: WorkOrderFacetsApi = { services: [], routes: [], periods: [], byDate: [], totalCount: 0, unassignedCount: 0 }

// Two calls: unfiltered feeds the dropdowns/route counts and never carries a date range (that would
// narrow those lists too); filtered (debounced) feeds the accordion months and header total.
export const useWorkOrderFacets = ({
  accountId,
  locationId,
  configuredServiceId,
  selectedServices,
  selectedRoutes,
  selectedWorkOrderStatuses,
  selectedDateRange,
}: Params) => {
  const scope = useMemo<WorkOrderFacetsRequestApi>(
    () => ({
      accountId: accountId as string,
      ...(locationId && { locationId }),
      ...(configuredServiceId && { configuredServiceIds: [configuredServiceId] }),
      // Every status, not the selection: this call feeds the option lists and counts.
      statuses: WORK_ORDER_STATUS_IDS,
    }),
    [accountId, locationId, configuredServiceId]
  )

  const {
    data: optionsData,
    isFetching: isOptionsFetching,
    isError: isOptionsError,
    error: optionsError,
  } = useFetchWorkOrderFacetsQuery(scope, { skip: !accountId })

  const options = optionsData ?? EMPTY_FACETS

  const servicesOptions = useMemo(() => options.services.map(service => ({ id: service.id, value: service.name })), [options.services])

  const { unassignedCount } = options

  const routesOptions = useMemo(() => {
    const assigned = options.routes.map(route => ({ id: route.id, value: route.name ?? '', routeColor: route.color ?? '' }))
    return unassignedCount > 0 ? [{ id: UNASSIGNED_ROUTE.id, value: UNASSIGNED_ROUTE.name }, ...assigned] : assigned
  }, [options.routes, unassignedCount])

  const routeCounts = useMemo(() => {
    const counts: Record<string, number> = { All: options.totalCount, [UNASSIGNED_ROUTE.id]: unassignedCount }
    options.routes.forEach(route => {
      counts[route.id] = route.count
    })
    return counts
  }, [options.routes, options.totalCount, unassignedCount])

  // An empty selection means "show nothing", the way the client-side filter did — never "no filter".
  const isAnyFilterCleared =
    servicesOptions.length > 0 &&
    (selectedServices?.length === 0 || selectedRoutes?.length === 0 || selectedWorkOrderStatuses?.length === 0)

  // Only the selection-derived fields are debounced. Spreading scope in here too would delay a
  // location change by the debounce, and RTK Query keeps serving the previous location's numbers
  // in that gap.
  const selectionArgs = useMemo<Omit<WorkOrderFacetsRequestApi, 'accountId'>>(() => {
    const args: Omit<WorkOrderFacetsRequestApi, 'accountId'> = {}

    if (!isEverythingSelected(selectedServices, servicesOptions.length)) {
      args.pricedServiceIds = (selectedServices ?? []).map(service => service.id)
    }

    if (!isEverythingSelected(selectedRoutes, routesOptions.length)) {
      const selected = selectedRoutes ?? []
      args.routeIds = selected.filter(route => route.id !== UNASSIGNED_ROUTE.id).map(route => route.id)
      args.includeUnassigned = selected.some(route => route.id === UNASSIGNED_ROUTE.id)
    }

    // Always sent, as fetchWorkOrderList does: omitted, the SQL counts statuses the work order list never shows.
    args.statuses = isEverythingSelected(selectedWorkOrderStatuses, WORK_ORDER_STATUS_FILTER_MENU_OPTIONS.length)
      ? WORK_ORDER_STATUS_IDS
      : (selectedWorkOrderStatuses ?? []).map(status => status.id)

    const [rangeStart, rangeEnd] = selectedDateRange ?? []
    if (rangeStart) {
      args.startDate = formatDateToBEFormatDateFns(rangeStart)
      args.endDate = formatDateToBEFormatDateFns(rangeEnd ?? rangeStart)
    }

    return args
  }, [selectedServices, selectedRoutes, selectedWorkOrderStatuses, selectedDateRange, servicesOptions.length, routesOptions.length])

  const debouncedSelection = useDebounced(selectionArgs, FILTER_DEBOUNCE_MS)

  const debouncedFilterArgs = useMemo<WorkOrderFacetsRequestApi>(() => ({ ...scope, ...debouncedSelection }), [scope, debouncedSelection])

  const {
    data: filteredData,
    isFetching: isFilteredFetching,
    isSuccess,
    isError,
    error: filteredError,
  } = useFetchWorkOrderFacetsQuery(debouncedFilterArgs, { skip: !accountId || isAnyFilterCleared })

  // RTK Query drops to undefined while a new set of filter args resolves. Holding the last response
  // keeps the accordion rendered under the loading overlay instead of collapsing.
  const lastFiltered = useRef<WorkOrderFacetsApi>(EMPTY_FACETS)
  if (filteredData) {
    lastFiltered.current = filteredData
  }
  useEffect(() => {
    lastFiltered.current = EMPTY_FACETS
  }, [scope])

  const filtered = isAnyFilterCleared ? EMPTY_FACETS : filteredData ?? lastFiltered.current

  return {
    servicesOptions,
    routesOptions,
    routeCounts,
    periods: filtered.periods,
    totalCount: filtered.totalCount,
    isOptionsFetching,
    isFetching: isOptionsFetching || isFilteredFetching,
    isFetched: isSuccess || isError || isAnyFilterCleared,
    // isFetched is true on failure too — a caller that ignores isError shows an empty state.
    isError,
    // Separate: the dropdowns degrade to empty on their own, so this only warrants reporting. Folding
    // it into isError would hide an accordion that loaded fine.
    isOptionsError,
    // Kept apart: collapsed into one field, whichever failed second is indistinguishable from the
    // first and a caller de-duping on identity drops it.
    error: filteredError,
    optionsError,
  }
}
